import React, { useState, useEffect, useRef } from "react";
import "./HomeInsurance.css";
import { Link } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";
import FaqSection from "../../common/faq-section/FaqSection";
import AOS from "aos";
import "aos/dist/aos.css";
import TestimonialsSection from "../../common/testimonials-section/TestimonialsSection";
import { useDispatch, useSelector } from "react-redux";
import {
  submitEnquiryForm,
  resetEnquiryFormState,
} from "../../store/slices/enquiryFormSlice";

const HomeInsurance = () => {
  const [formData, setFormData] = useState({
    fullname: "",
    phone: "",
    email: "",
    referral_name: "",
    referral_email: "",
    message: "",
  });

  const [acceptTerms, setAcceptTerms] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState("");
  const [submitStatus, setSubmitStatus] = useState("");
  const [globalError, setGlobalError] = useState("");

  const [recaptchaToken, setCaptchaValue] = useState(null);

  const recaptchaRef = useRef(null);

  // Initialize AOS on component mount
  useEffect(() => {
    // Add a slight delay to ensure React finishes painting the DOM
    const initAOS = setTimeout(() => {
      AOS.init({
        duration: 1000,
        once: true,
        offset: 100,
        easing: "ease-in-out",
      });
    }, 100);

    return () => {
      clearTimeout(initAOS);
      AOS.refresh();
    };
  }, []);

  // Home Insurance Calculator state
  const [calculatorData, setCalculatorData] = useState({
    propertyArea: "", // In sq ft
    constructionCost: "2000", // Average cost per sq ft
    contentValue: "",
    securityLevel: "standard",
  });
  const handleRecaptchaChange = (value) => {
    setCaptchaValue(value);

    if (errors.captcha) {
      setErrors((prev) => ({
        ...prev,
        captcha: "",
      }));
    }
  };

  // Validation function
  const validateForm = () => {
    const newErrors = {};

    if (!formData.fullname.trim()) {
      newErrors.fullname = "Full name is required";
    } else if (formData.fullname.trim().length < 2) {
      newErrors.fullname = "Name must be at least 2 characters";
    }

    if (!formData.phone.trim()) {
      newErrors.phone = "Mobile number is required";
    } else if (!/^[6-9]\d{9}$/.test(formData.phone)) {
      newErrors.phone = "Please enter a valid 10-digit Indian mobile number";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email is invalid";
    }

    if (formData.referral_email) {
      if (!/\S+@\S+\.\S+/.test(formData.referral_email)) {
        newErrors.referral_email = "Referral email is invalid";
      } else if (
        formData.email.toLowerCase() === formData.referral_email.toLowerCase()
      ) {
        newErrors.referral_email =
          "Referral email cannot be the same as your email";
      }
    }

    if (!acceptTerms) {
      newErrors.acceptTerms = "You must agree to the terms and conditions";
    }

    if (!recaptchaToken) {
      newErrors.recaptcha = "Please complete the reCAPTCHA verification";
    }

    setErrors(newErrors);
    // return true if no errors
    return Object.keys(newErrors).length === 0;
  };

  const [errors, setErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleCalculatorChange = (e) => {
    const { name, value } = e.target;
    setCalculatorData((prev) => ({ ...prev, [name]: value }));
  };

  const calculateHomeCoverage = () => {
    const area = parseFloat(calculatorData.propertyArea) || 0;
    const cost = parseFloat(calculatorData.constructionCost) || 0;
    const contents = parseFloat(calculatorData.contentValue) || 0;

    // Structure Value + Contents
    let totalValue = area * cost + contents;

    if (totalValue === 0) return "₹ 0";
    if (totalValue >= 10000000)
      return `₹ ${(totalValue / 10000000).toFixed(2)} Crore`;
    return `₹ ${(totalValue / 100000).toFixed(2)} Lakh`;
  };
  const clearMessageAfterTimeout = (timeout = 5000) => {
    setTimeout(() => {
      setSubmitMessage("");
      setSubmitStatus("");
    }, timeout);
  };

  const dispatch = useDispatch();
  const { success, error, message } = useSelector((state) => state.enquiryForm);

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      setSubmitMessage("Please fix the errors in the form");
      setSubmitStatus("error");
      return;
    }

    setIsSubmitting(true);
    setSubmitMessage("");

    const payload = {
      fullname: formData.fullname,
      email: formData.email,
      mobile: formData.phone,
      require_service: "Home Insurance",
      referral_name: formData.referral_name,
      referral_email: formData.referral_email,
      message: formData.message,
      agreeToTerms: acceptTerms,
      recaptchaToken: recaptchaToken,
    };

    dispatch(submitEnquiryForm(payload));
  };
  useEffect(() => {
    if (success) {
      setSubmitMessage("Form submitted successfully ✅");
      setSubmitStatus("success");

      setFormData({
        fullname: "",
        phone: "",
        email: "",

        message: "",
      });

      setAcceptTerms(false);

      setCaptchaValue(null);
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }

      clearMessageAfterTimeout();
      setIsSubmitting(false);

      dispatch(resetEnquiryFormState());
    }
  }, [success, message]); // ✅ add message
  useEffect(() => {
    if (error) {
      setSubmitMessage(error);
      setSubmitStatus("error");

      clearMessageAfterTimeout();
      setIsSubmitting(false);
    }
  }, [error]);
  const accordionData = [
    {
      id: 1,
      title: "What does Home Insurance cover?",
      content: [
        "Home insurance provides financial protection against disasters. A standard policy insures the home itself and the things you keep in it.",
        "It covers losses caused by Fire, Lightning, Earthquakes, Storms, and Floods.",
        "It also provides coverage against Burglary, Theft, and even Third-party liability.",
      ],
    },
    {
      id: 2,
      title: "Types of Home Insurance Coverage",
      types: [
        {
          name: "Structure Insurance",
          description: "Covers the physical building and permanent fixtures.",
          features: [
            "Fire protection",
            "Natural calamities",
            "Explosion/Implosion",
          ],
        },
        {
          name: "Content Insurance",
          description:
            "Protects valuables inside the house (Appliances, Jewelry, Furniture).",
          features: [
            "Burglary cover",
            "Mechanical breakdown",
            "Accidental damage",
          ],
        },
        {
          name: "Tenant's Insurance",
          description:
            "Specifically for those living in rented homes to protect personal belongings.",
          features: [
            "Low premium",
            "Portable assets cover",
            "Personal liability",
          ],
        },
      ],
    },
  ];

  const reasons = [
    {
      title: "Protects Against Natural Disasters",
      desc: "India faces frequent extreme weather events like floods, cyclones, earthquakes, and storms, which can cause severe damage to homes. Home insurance ensures financial assistance after unforeseen disasters.",
    },
    {
      title: "Provides Coverage for Theft and Burglary",
      desc: "Burglaries or vandalism can lead to the loss or damage of valuables such as electronics, jewellery and furniture. A home insurance policy helps compensate for these losses, allowing you to replace or repair your belongings.",
    },
    {
      title: "Protects Valuable Personal Belongings",
      desc: "Home insurance doesn't just cover your home structure; it also protects personal items inside, like electronics, appliances, artwork, and furniture against damage or theft, depending on your policy.",
    },
    {
      title: "Affordable Protection for a Major Asset",
      desc: "Home Insurance is an affordable investment and a smart financial move to avoid incurring massive out-of-pocket costs in the event of unexpected damage or loss to your home and possessions.",
    },
    {
      title: "Financial Security for Future Stability",
      desc: "Without home insurance, paying for repairs after a disaster, burglary, or accident can derail long-term financial plans. Home Coverage ensures that major expenses are handled without affecting savings for education, retirement, or other life goals.",
    },
    {
      title: "Peace of Mind",
      desc: "Living with financial uncertainty about natural disasters, theft, or accidents can be stressful. Home insurance provides peace of mind, knowing that your property and belongings are always financially protected.",
    },
  ];

  const stats = [
    {
      id: 1,
      text: "In the first nine months of 2024, extreme weather events in India destroyed 235,862 houses and buildings.",
    },
    {
      id: 2,
      text: "According to a report, India experienced extreme weather events on 86% of the days in the first nine months.",
    },
    {
      id: 3,
      text: "A burglary, robbery, or break-in occurs every 3 minutes in India, highlighting the need for home security measures.",
    },
    {
      id: 4,
      text: "In 2022, India reported over 652,000 theft cases, representing a significant increase compared to previous years. This was a significant rise in cases compared to the last fiscal year.",
    },
    {
      id: 5,
      text: "Approximately 59% of India's landmass is vulnerable to earthquakes, with major cities like Delhi, Mumbai, and Kolkata falling in high-risk seismic zones.",
    },
    {
      id: 6,
      text: "Over 85% of Indian districts are exposed to extreme climate events, such as floods, droughts, and cyclones, which increases the risk of property damage.",
    },
  ];

  const categories = [
    {
      title: "Individual Apartment",
      icon: "🏢",
      desc: "Those who live in independent flats, whether part of a housing society or standalone buildings, must have home insurance. The same could be either a flat that you own or rent. KFS policy is suited for both!",
    },
    {
      title: "Independent Building",
      icon: "🏘️",
      desc: "Perhaps you and your extended family live in a standalone building, owning or renting flats in the entire building. In this case, you can choose to cover for them all with a home insurance by KFS.",
    },
    {
      title: "Independent Villa",
      icon: "🏡",
      desc: "Suppose you own or have rented an independent villa or house. In that case, home insurance is essential to protect your property and its contents from potential risks such as burglary, flooding, storms, and other unforeseen circumstances.",
    },
  ];

  const coverageItems = [
    {
      title: "Fire",
      desc: "Fire is dreadful and can lead to plenty of damage and losses to your home and its contents. Our policy will be there for you.",
      icon: "🔥",
    },
    {
      title: "Explosion & Aircraft Damage",
      desc: "Damages and losses caused by explosions or even aircraft damage will be covered for too in our house insurance.",
      icon: "💥",
    },
    {
      title: "Storms",
      desc: "Covers your house and your belongings from dire storms that could lead to potential damages and losses.",
      icon: "🌪️",
    },
    {
      title: "Floods",
      desc: "Protects your house from losses and damages when the rains are out of control, leading to floods that impact your home.",
      icon: "🌊",
    },
    {
      title: "Earthquakes",
      desc: "Nobody can avoid nature's furies, but you can ensure you're covered for any potential losses from earthquakes.",
      icon: "🏘️",
    },
    {
      title: "Theft and Burglary",
      desc: "Burglaries and thefts can occur at any time, making it sensible to ensure your property is secure with home insurance.",
      icon: "👤",
    },
  ];

  const benefits = [
    {
      title: "Value for Money",
      desc: "We ensure your home is covered and the premium is affordable for you and under your budget!",
    },
    {
      title: "KFSal Friendly",
      desc: "With KFS, everything from buying a home insurance policy to making claims is simple and can be done online! (Note: Claims above 1 lakh will need manual inspection as per the IRDAI).",
    },
    {
      title: "Comprehensive Protection of Your Home",
      desc: "Natural calamities, such as earthquakes or floods, as well as theft and burglaries, can occur at any time. Home insurance covers all of these situations and more.",
    },
    {
      title: "Tenant and Owner Security",
      desc: "You can discover a house insurance policy that safeguards your safe haven, whether you're a renter or a homeowner.",
    },
    {
      title: "24x7 Customer Support",
      desc: "Emergencies come unannounced and can happen at any point in time! Hence, we are always a ring away, no matter the time or day!",
    },
  ];

  // WhyHomeInsurance component as a nested component
  const WhyHomeInsurance = () => {
    return (
      <section className="kfs-why-hi-section">
        <div className="kfs-why-hi-container">
          <div
            className="kfs-why-hi-header"
            data-aos="fade-up"
            data-aos-duration="800"
          >
            <h2>Why Do You Need Home Insurance?</h2>
            <p>
              Home insurance is essential because it provides financial
              protection against unexpected events that could damage or destroy
              your home and belongings. Here are some reasons why you must have
              home insurance:
            </p>
          </div>

          <div className="kfs-why-hi-grid">
            {reasons.map((item, index) => (
              <div
                className="kfs-why-hi-card"
                key={index}
                data-aos="fade-up"
                data-aos-delay={100 + index * 50}
                data-aos-duration="600"
              >
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  };

  // HomeInsuranceStats component as a nested component
  const HomeInsuranceStats = () => {
    return (
      <section className="kfs-stat-section">
        <div className="kfs-stat-container">
          <div
            className="kfs-stat-header"
            data-aos="fade-up"
            data-aos-duration="800"
          >
            <h2>“You Think Home Insurance Isn’t Necessary?”</h2>
            <p className="kfs-stat-subtitle">
              If you’re still thinking so, read on...
            </p>
          </div>

          <div className="kfs-stat-grid">
            {stats.map((item, index) => (
              <div
                className="kfs-stat-item"
                key={item.id}
                data-aos="fade-left"
                data-aos-delay={index * 100}
                data-aos-duration="600"
              >
                <span className="kfs-stat-number">{item.id}</span>
                <p className="kfs-stat-text">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  };

  // HomeTypes component as a nested component
  const HomeTypes = () => {
    return (
      <section className="kfs-ht-section">
        <div className="kfs-ht-container">
          <div
            className="kfs-ht-header"
            data-aos="fade-up"
            data-aos-duration="800"
          >
            <h2>Types of Buildings Covered Under Home Insurance</h2>
            <p>
              From independent owned homes to rented apartments; a home
              insurance by KFS is designed to suit all types of houses.
            </p>
          </div>

          <div className="kfs-ht-grid">
            {categories.map((item, index) => (
              <div
                className="kfs-ht-card"
                key={index}
                data-aos="zoom-in"
                data-aos-delay={index * 150}
                data-aos-duration="600"
              >
                <div className="kfs-ht-icon-box">
                  <span className="kfs-ht-icon-placeholder">{item.icon}</span>
                </div>
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  };

  // WhatIsCovered component
  const WhatIsCovered = () => {
    return (
      <section className="kfs-hi-section kfs-coverage-section">
        <div className="kfs-hi-container">
          <div
            className="kfs-hi-header"
            data-aos="fade-up"
            data-aos-duration="800"
          >
            <h2>What's Covered in Home Insurance by KFS?</h2>
          </div>
          <div className="kfs-hi-grid">
            {coverageItems.map((item, index) => (
              <div
                className="kfs-hi-card"
                key={index}
                data-aos="flip-up"
                data-aos-delay={index * 100}
                data-aos-duration="700"
              >
                <div className="kfs-hi-card-visual">{item.icon}</div>
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  };

  const HomeInsuranceExclusions = () => {
    const exclusions = [
      {
        title: "Deliberate Damage",
        desc: "The policy does not cover any intentional destruction of the house by the owner or occupants.",
      },
      {
        title: "War and Hostilities",
        desc: "Losses resulting from war, invasions, or war-like operations do not fall under the coverage.",
      },
      {
        title: "Contamination and Radiation",
        desc: "Damages caused by ionising radiation or radioactive contamination are excluded.",
      },
      {
        title: "Excluded Items",
        desc: "The policy does not cover bullion, unset precious stones, manuscripts, vehicles, or explosive substances.",
      },
      {
        title: "Claim Processing Fees",
        desc: "Any expenses incurred for preparing a claim, such as documentation fees, are not reimbursed.",
      },
      {
        title: "Structural Modifications",
        desc: "Those expenses will not be covered if the house undergoes alterations exceeding 10% of its carpet area (from the commencement date or on the date of renewal).",
      },
    ];

    return (
      <section className="kfs-hie-section">
        <div className="kfs-hie-container">
          <div
            className="kfs-hie-header"
            data-aos="fade-up"
            data-aos-duration="800"
          >
            <h2>What’s Not Covered?</h2>
            <p>
              Bharat Griha Raksha policy does not cover damages caused by the
              reasons mentioned below:
            </p>
          </div>

          <div className="kfs-hie-grid">
            {exclusions.map((item, index) => (
              <div
                className="kfs-hie-card"
                key={index}
                data-aos="fade-right"
                data-aos-delay={index * 80}
                data-aos-duration="600"
              >
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  };

  // KFSBenefits component
  const KFSBenefits = () => {
    return (
      <section className="kfs-db-section">
        <div className="kfs-db-container">
          <div
            className="kfs-db-visual"
            data-aos="fade-right"
            data-aos-duration="800"
          >
            <div className="kfs-db-house-wrapper">
              <img src="./images/insurance/home2.png" alt="Home Protection" />
              <img src="./images/insurance/home3.png" alt="Home Protection" />
              <img src="./images/insurance/home4.png" alt="Home Protection" />
              <div className="kfs-db-dot kfs-db-dot-1"></div>
              <div className="kfs-db-dot kfs-db-dot-2"></div>
              <div className="kfs-db-dot kfs-db-dot-3"></div>
            </div>
          </div>

          <div
            className="kfs-db-content"
            data-aos="fade-left"
            data-aos-duration="800"
            data-aos-delay="200"
          >
            <header className="kfs-db-header">
              <h2>What's Great About Home Insurance by KFS?</h2>
              <p>
                Go KFS,{" "}
                <span className="kfs-db-highlight">
                  Bharat Griha Raksha policy
                </span>{" "}
                is great because it offers the below mentioned benefits:
              </p>
            </header>

            <div className="kfs-db-benefit-list">
              {benefits.map((item, index) => (
                <div
                  className="kfs-db-item"
                  key={index}
                  data-aos="fade-up"
                  data-aos-delay={index * 100}
                  data-aos-duration="500"
                >
                  <h4>{item.title}</h4>
                  <p>{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  };

  const HowToBuy = () => {
    const steps = [
      {
        title: "Visit and Select Property Type:",
        desc: "Go to KFS's Bharat Griha Raksha Insurance Policy page or download the KFS App. Select your property type and enter your PIN code and mobile number.",
      },
      {
        title: "View Prices and Enter Details:",
        desc: "Click ‘View Prices’, enter your plan and home building details, and choose your plan.",
      },
      {
        title: "Fill in Personal Details:",
        desc: "Provide the property owner's name, mobile number, email ID, and PAN card number.",
      },
      {
        title: "Payment and KYC:",
        desc: "Pay online via Card, Net Banking, UPI, Wallet, or EMI. Complete KYC to issue your policy.",
      },
    ];

    return (
      <section className="kfs-htb-section">
        <div className="kfs-htb-container">
          <header
            className="kfs-htb-header"
            data-aos="fade-up"
            data-aos-duration="800"
          >
            <h2>How to Buy Home Insurance from KFS Online?</h2>
            <p>
              Whether you’re buying your insurance policy from our mobile app or
              website, you can follow these easy steps and secure your home.
            </p>
          </header>

          <div className="kfs-htb-content">
            {/* Left Side: Illustration Card */}
            <div
              className="kfs-htb-visual"
              data-aos="fade-right"
              data-aos-duration="800"
            >
              <div className="kfs-htb-image-card">
                <img
                  src="./images/insurance/home5.png"
                  alt="Family secured with home insurance"
                />
              </div>
            </div>

            {/* Right Side: Step-by-Step Timeline */}
            <div
              className="kfs-htb-timeline"
              data-aos="fade-left"
              data-aos-duration="800"
              data-aos-delay="200"
            >
              {steps.map((step, index) => (
                <div
                  className="kfs-htb-step"
                  key={index}
                  data-aos="fade-up"
                  data-aos-delay={index * 150}
                  data-aos-duration="600"
                >
                  <div className="kfs-htb-step-dot"></div>
                  <div className="kfs-htb-step-content">
                    <h3>{step.title}</h3>
                    <p>{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  };

  const ClaimProcess = () => {
    const stepsData = [
      {
        id: 1,
        icon: (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width={70}
            viewBox="0 0 640 640"
          >
            <path d="M508.3 113.9C495.6 102.2 444.2 64.9 329.6 64.4C329.6 64.4 194.5 56.3 128.7 116.7C92.1 153.3 79.2 207 77.8 273.5C76.4 340 74.7 464.6 194.8 498.4L194.9 498.4L194.8 550C194.8 550 194 570.9 207.8 575.1C224.4 580.3 234.2 564.4 250.1 547.3C258.8 537.9 270.8 524.1 279.9 513.6C362.1 520.5 425.2 504.7 432.4 502.4C449 497 542.9 485 558.1 360.4C573.9 231.8 550.5 150.6 508.3 113.9zM522.2 351C509.3 455 433.2 461.6 419.2 466.1C413.2 468 357.7 481.8 288 477.3C288 477.3 236 540 219.8 556.3C214.5 561.6 208.7 561.1 208.8 550.6C208.8 543.7 209.2 464.9 209.2 464.9L209.2 464.9C107.4 436.7 113.4 330.6 114.5 275.1C115.6 219.6 126.1 174.1 157.1 143.5C212.8 93 327.5 100.5 327.5 100.5C424.4 100.9 470.8 130.1 481.6 139.9C517.3 170.5 535.5 243.7 522.2 351zM383.2 270.2C383.6 278.8 370.7 279.4 370.3 270.8C369.2 248.8 358.9 238.1 337.7 236.9C329.1 236.4 329.9 223.5 338.4 224C366.3 225.5 381.8 241.5 383.2 270.2zM403.5 281.5C404.5 239.1 378 205.9 327.7 202.2C319.2 201.6 320.1 188.7 328.6 189.3C386.6 193.5 417.5 233.4 416.4 281.8C416.3 290.4 403.3 290 403.5 281.5zM450.5 294.9C450.6 303.5 437.6 303.6 437.6 295C437 213.5 382.7 169.1 316.8 168.6C308.3 168.5 308.3 155.7 316.8 155.7C390.5 156.2 449.8 207.1 450.5 294.9zM439.2 393L439.2 393.2C428.4 412.2 408.2 433.2 387.4 426.5L387.2 426.2C366.1 420.3 316.4 394.7 285 369.7C268.8 356.9 254 341.8 242.6 327.3C232.3 314.4 221.9 299.1 211.8 280.7C190.5 242.2 185.8 225 185.8 225C179.1 204.2 200 184 219.1 173.2L219.3 173.2C228.5 168.4 237.3 170 243.2 177.1C243.2 177.1 255.6 191.9 260.9 199.2C265.9 206 272.6 216.9 276.1 223C282.2 233.9 278.4 245 272.4 249.6L260.4 259.2C254.3 264.1 255.1 273.2 255.1 273.2C255.1 273.2 272.9 340.5 339.4 357.5C339.4 357.5 348.5 358.3 353.4 352.2L363 340.2C367.6 334.2 378.7 330.4 389.6 336.5C404.3 344.8 423 357.7 435.4 369.4C442.4 375.1 444 383.8 439.2 393z" />
          </svg>
        ),
        text: "Call us at 9909002220. We will assist you in filing a claim and investigating the loss or damage as necessary.",
      },
      {
        id: 2,
        icon: (
          <svg
            width="120"
            height="120"
            viewBox="0 0 100 100"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect
              x="35"
              y="25"
              width="30"
              height="40"
              rx="3"
              stroke="#444444"
              strokeWidth="3"
              fill="none"
            />
            <path
              d="M42 22H58V28H42V22Z"
              stroke="#444444"
              strokeWidth="3"
              fill="none"
            />
            <path
              d="M45 22C45 20.3431 46.3431 19 48 19H52C53.6569 19 55 20.3431 55 22"
              stroke="#444444"
              strokeWidth="3"
              fill="none"
            />
            <line
              x1="42"
              y1="38"
              x2="58"
              y2="38"
              stroke="#ffb800"
              strokeWidth="3"
              strokeLinecap="round"
            />
            <line
              x1="42"
              y1="46"
              x2="58"
              y2="46"
              stroke="#ffb800"
              strokeWidth="3"
              strokeLinecap="round"
            />
            <line
              x1="42"
              y1="54"
              x2="52"
              y2="54"
              stroke="#ffb800"
              strokeWidth="3"
              strokeLinecap="round"
            />
            <line
              x1="30"
              y1="70"
              x2="70"
              y2="70"
              stroke="#444444"
              strokeWidth="3"
              strokeLinecap="round"
            />
          </svg>
        ),
        text: "Upload the required documents & your bank account details, on the link sent.",
      },
      {
        id: 3,
        icon: (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width={70}
            viewBox="0 0 640 640"
          >
            <path d="M300.9 149.2L184.3 278.8C179.7 283.9 179.9 291.8 184.8 296.7C215.3 327.2 264.8 327.2 295.3 296.7L327.1 264.9C331.3 260.7 336.6 258.4 342 258C348.8 257.4 355.8 259.7 361 264.9L537.6 440L608 384L608 96L496 160L472.2 144.1C456.4 133.6 437.9 128 418.9 128L348.5 128C347.4 128 346.2 128 345.1 128.1C328.2 129 312.3 136.6 300.9 149.2zM148.6 246.7L255.4 128L215.8 128C190.3 128 165.9 138.1 147.9 156.1L144 160L32 96L32 384L188.4 514.3C211.4 533.5 240.4 544 270.3 544L286 544L279 537C269.6 527.6 269.6 512.4 279 503.1C288.4 493.8 303.6 493.7 312.9 503.1L353.9 544.1L362.9 544.1C382 544.1 400.7 539.8 417.7 531.8L391 505C381.6 495.6 381.6 480.4 391 471.1C400.4 461.8 415.6 461.7 424.9 471.1L456.9 503.1L474.4 485.6C483.3 476.7 485.9 463.8 482 452.5L344.1 315.7L329.2 330.6C279.9 379.9 200.1 379.9 150.8 330.6C127.8 307.6 126.9 270.7 148.6 246.6z" />
          </svg>
        ),
        text: "After verification, the approved claim amount is reimbursed to you.",
      },
    ];

    return (
      <section className="kfs-claim-section">
        <div className="kfs-claim-container">
          <div
            className="kfs-claim-header"
            data-aos="fade-up"
            data-aos-duration="800"
          >
            <h2>How to File a Home Insurance Claim at KFS?</h2>
            <p>
              Filing a claim with KFS is a quick, simple and hassle-free
              process. We've listed out what you can do to file a claim with us:
            </p>
          </div>

          {/* Stepper Timeline */}
          <div
            className="kfs-claim-stepper"
            data-aos="fade-up"
            data-aos-duration="600"
            data-aos-delay="200"
          >
            {stepsData.map((step, index) => (
              <React.Fragment key={step.id}>
                <div className="kfs-step-circle">{step.id}</div>
                {index < stepsData.length - 1 && (
                  <div className="kfs-step-line"></div>
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Cards Grid */}
          <div className="kfs-claim-cards-container">
            {stepsData.map((step, index) => (
              <div
                className="kfs-claim-card"
                key={step.id}
                data-aos="zoom-in"
                data-aos-delay={index * 150}
                data-aos-duration="700"
              >
                <div className="kfs-card-icon-wrapper">{step.icon}</div>
                <div className="kfs-card-text-wrapper">
                  <p>{step.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  };

  return (
    <div className="kfs-home-insurance-wrapper">
      {/* Hero Section */}
      <section className="kfs-hi-hero-section">
        <div className="kfs-hi-container">
          <div className="kfs-hi-hero-content">
            <div
              className="kfs-hi-hero-text"
              data-aos="fade-right"
              data-aos-duration="1000"
            >
              <span
                className="kfs-hi-badge"
                data-aos="fade-up"
                data-aos-delay="100"
              >
                Protect Your Most Valuable Asset
              </span>
              <h1
                className="kfs-hi-main-title"
                data-aos="fade-up"
                data-aos-delay="200"
              >
                <span className="kfs-hi-title-line">Home</span>
                <span className="kfs-hi-title-line kfs-accent">Insurance</span>
              </h1>
              <p
                className="kfs-hi-hero-description"
                data-aos="fade-up"
                data-aos-delay="300"
              >
                Comprehensive protection for your structure and belongings. Get
                covered against fire, theft, and natural disasters today.
              </p>
              <div
                className="kfs-hi-stats"
                data-aos="fade-up"
                data-aos-delay="400"
              >
                <div className="kfs-hi-stat-item">
                  <span className="kfs-hi-stat-value">₹2k/yr</span>
                  <span className="kfs-hi-stat-label">Starting at</span>
                </div>
                <div className="kfs-hi-stat-item">
                  <span className="kfs-hi-stat-value">Instant</span>
                  <span className="kfs-hi-stat-label">Policy Issue</span>
                </div>
              </div>
            </div>
            <div
              className="kfs-hi-hero-visual"
              data-aos="fade-left"
              data-aos-duration="1000"
              data-aos-delay="200"
            >
              <img
                src="./images/insurance/home1.png"
                alt="Home Protection"
                className="kfs-hi-main-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Main Content & Calculator */}
      <section className="kfs-hi-main-section">
        <div className="kfs-hi-container">
          <div className="kfs-hi-main-grid">
            <div className="kfs-hi-left-column">
              <div
                className="kfs-hi-calculator-card"
                data-aos="fade-up"
                data-aos-duration="800"
              >
                <h3>Home Value Estimator</h3>
                <div
                  className="kfs-hi-calc-row"
                  data-aos="fade-up"
                  data-aos-delay="100"
                >
                  <label>Property Area (Sq. Ft.)</label>
                  <input
                    type="number"
                    name="propertyArea"
                    value={calculatorData.propertyArea}
                    onChange={handleCalculatorChange}
                    placeholder="e.g. 1200"
                  />
                </div>
                <div
                  className="kfs-hi-calc-row"
                  data-aos="fade-up"
                  data-aos-delay="150"
                >
                  <label>Construction Cost (per Sq. Ft.)</label>
                  <select
                    name="constructionCost"
                    value={calculatorData.constructionCost}
                    onChange={handleCalculatorChange}
                  >
                    <option value="1800">Basic (₹1,800)</option>
                    <option value="2500">Premium (₹2,500)</option>
                    <option value="3500">Luxury (₹3,500)</option>
                  </select>
                </div>
                <div
                  className="kfs-hi-calc-row"
                  data-aos="fade-up"
                  data-aos-delay="200"
                >
                  <label>Content Value (Optional)</label>
                  <input
                    type="number"
                    name="contentValue"
                    value={calculatorData.contentValue}
                    onChange={handleCalculatorChange}
                    placeholder="e.g. 500000"
                  />
                </div>
                <div
                  className="kfs-hi-calc-result"
                  data-aos="zoom-in"
                  data-aos-delay="300"
                >
                  <span>Estimated Cover Needed</span>
                  <strong>{calculateHomeCoverage()}</strong>
                </div>
              </div>
            </div>

            <div className="kfs-hi-right-column">
              <div
                className="kfs-hi-form-card"
                data-aos="fade-up"
                data-aos-duration="800"
                data-aos-delay="200"
              >
                <h3>Get a Custom Quote</h3>
                {submitMessage && (
                  <div
                    className={`kfs-hi-submit-message ${submitStatus}`}
                    style={{
                      backgroundColor:
                        submitStatus === "success" ? "#d4edda" : "#f8d7da",
                      color: submitStatus === "success" ? "#155724" : "#721c24",
                      padding: "12px",
                      borderRadius: "4px",
                      marginBottom: "20px",
                      textAlign: "center",
                    }}
                  >
                    {submitMessage}
                  </div>
                )}
                {globalError && (
                  <div
                    className="kfs-hi-error-message"
                    style={{
                      backgroundColor: "#f8d7da",
                      color: "#721c24",
                      padding: "12px",
                      borderRadius: "4px",
                      marginBottom: "20px",
                      textAlign: "center",
                    }}
                  >
                    {globalError}
                  </div>
                )}
                <form onSubmit={handleSubmit} className="kfs-hi-form">
                  <input
                    type="text"
                    name="fullname"
                    placeholder="Full Name *"
                    value={formData.fullname}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                    data-aos="fade-up"
                    data-aos-delay="250"
                  />
                  {errors.fullname && (
                    <span
                      className="kfs-hi-error-text"
                      style={{
                        color: "#dc3545",
                        fontSize: "12px",
                        marginTop: "-10px",
                        marginBottom: "10px",
                        display: "block",
                      }}
                    >
                      {errors.fullname}
                    </span>
                  )}

                  <input
                    type="tel"
                    name="phone"
                    placeholder="Mobile Number *"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                    maxLength="10"
                    data-aos="fade-up"
                    data-aos-delay="300"
                  />
                  {errors.phone && (
                    <span
                      className="kfs-hi-error-text"
                      style={{
                        color: "#dc3545",
                        fontSize: "12px",
                        marginTop: "-10px",
                        marginBottom: "10px",
                        display: "block",
                      }}
                    >
                      {errors.phone}
                    </span>
                  )}

                  <input
                    type="email"
                    name="email"
                    placeholder="Email Address *"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                    data-aos="fade-up"
                    data-aos-delay="350"
                  />
                  {errors.email && (
                    <span
                      className="kfs-hi-error-text"
                      style={{
                        color: "#dc3545",
                        fontSize: "12px",
                        marginTop: "-10px",
                        marginBottom: "10px",
                        display: "block",
                      }}
                    >
                      {errors.email}
                    </span>
                  )}

                  <textarea
                    name="message"
                    placeholder="Property Location or specific details..."
                    value={formData.message}
                    onChange={handleInputChange}
                    rows="3"
                    disabled={isSubmitting}
                    data-aos="fade-up"
                    data-aos-delay="400"
                  ></textarea>

                  <div className="li-recaptcha">
                    <ReCAPTCHA
                      ref={recaptchaRef}
                      sitekey="6Ldk_IksAAAAAO8Aq02xgOPeBlszLT2K30hdJCjM"
                      onChange={handleRecaptchaChange}
                      // ✅ ADD THIS (important)
                      onExpired={() => {
                        setCaptchaValue(null);
                        setErrors((prev) => ({
                          ...prev,
                          captcha: "Captcha expired, please verify again",
                        }));
                      }}
                      // ✅ OPTIONAL (network issue handle)
                      onErrored={() => {
                        setCaptchaValue(null);
                        setErrors((prev) => ({
                          ...prev,
                          captcha: "Captcha failed to load. Try again.",
                        }));
                      }}
                    />
                  </div>

                  <div className="li-terms-group">
                    <input
                      type="checkbox"
                      id="kfsConsent"
                      name="kfsConsent"
                      checked={acceptTerms}
                      onChange={(e) => {
                        setAcceptTerms(e.target.checked);
                        if (errors.acceptTerms) {
                          setErrors((prev) => ({ ...prev, acceptTerms: "" }));
                        }
                      }}
                      disabled={isSubmitting}
                    />
                    <label htmlFor="kfsConsent">
                      I agree to receive loan updates and promotional from{" "}
                      <strong>Krishana Finconcept</strong> via WhatsApp and
                      other accept the{" "}
                      <a
                        href="/privacy-security-policy"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Terms & Conditions
                      </a>
                    </label>
                  </div>
                  {errors.acceptTerms && (
                    <span
                      className="kfs-hi-error-text"
                      style={{
                        color: "#dc3545",
                        fontSize: "12px",
                        marginTop: "-10px",
                        marginBottom: "10px",
                        display: "block",
                      }}
                    >
                      {errors.acceptTerms}
                    </span>
                  )}

                  <button
                    type="submit"
                    className="kfs-hi-submit-btn"
                    disabled={!acceptTerms || isSubmitting}
                    style={{
                      opacity: !acceptTerms || isSubmitting ? 0.7 : 1,
                      cursor:
                        !acceptTerms || isSubmitting
                          ? "not-allowed"
                          : "pointer",
                    }}
                    data-aos="fade-up"
                    data-aos-delay="550"
                  >
                    {isSubmitting ? "Processing..." : "Secure My Home →"}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="kfs-hi-info-container">
        <div className="kfs-hi-info-wrapper">
          <header
            className="kfs-hi-info-header"
            data-aos="fade-down"
            data-aos-duration="800"
          >
            <h1>Buy Home Insurance Online in India starting from ₹150/year*</h1>
          </header>

          <div className="kfs-hi-info-body">
            <p
              className="kfs-hi-lead-text"
              data-aos="fade-up"
              data-aos-duration="800"
              data-aos-delay="200"
            >
              Home insurance, also known as homeowners insurance, is a type of
              property insurance that provides financial protection against
              losses and damages to an individual's house and the assets inside
              it. From your expensive gadgets and beautiful interiors to your
              jewellery and other valuable belongings, home insurance protects
              everything.
            </p>

            <article
              className="kfs-hi-info-section"
              data-aos="fade-up"
              data-aos-duration="800"
              data-aos-delay="300"
            >
              <h2>What is Home Insurance?</h2>
              <p>
                Home insurance is a policy that helps you cover your own home or
                rented apartment, as well as your personal belongings within,
                from unforeseen circumstances such as burglary, fire, flood,
                storm, and explosion.
              </p>

              <p>
                This type of insurance is especially important for homeowners
                who want to safeguard their investment and ensure they are not
                left with significant out-of-pocket expenses after a disaster.
                Our <strong>Go KFS, Bharat Griha Raksha Policy</strong> will
                help you secure your home and valuables.
              </p>

              <p>
                To ensure that your home is secured against burglaries as well,
                you can combine the KFS Burglary Insurance policy with your Home
                Insurance policy.
              </p>
            </article>
          </div>
        </div>
      </section>
      {WhyHomeInsurance()}
      {HomeInsuranceStats()}
      {HomeTypes()}
      {WhatIsCovered()}
      {HomeInsuranceExclusions()}
      {KFSBenefits()}
      {HowToBuy()}
      {ClaimProcess()}
      <TestimonialsSection />
    </div>
  );
};

export default HomeInsurance;
